import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'motion/react';
import {
  Library as LibraryIcon,
  Search,
  Users,
  CheckCircle2,
  XCircle,
  BarChart3,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Loader2,
  BookOpen,
} from 'lucide-react';
import { libraryApi } from '../../lib/api/libraryApi';
import { useAuth } from '../../contexts/AuthContext';

const Library = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 12;

  // Debounce search
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['library-questions-stats', page, debouncedSearch, typeFilter],
    queryFn: () =>
      libraryApi.getQuestionsStats({
        page,
        limit,
        search: debouncedSearch || undefined,
        type: typeFilter || undefined,
      }),
    staleTime: 60 * 1000,
    enabled: !!user,
  });

  const questions = data?.questions || [];
  const pagination = data?.pagination || { current: 1, total: 1, totalCount: 0 };

  const truncate = (str, maxLen) => {
    if (!str) return '';
    if (str.length <= maxLen) return str;
    return str.slice(0, maxLen) + '...';
  };

  const formatType = (t) => {
    if (!t) return '—';
    return t.replace(/\([^)]*\)/g, '').trim() || t;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/50"
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 pt-6 pb-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-[#2563eb]/10">
              <LibraryIcon className="h-8 w-8 text-[#2563eb]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                Question Library
              </h1>
              <p className="text-gray-600 text-sm sm:text-base mt-0.5">
                Browse your questions with usage stats across screenings and candidates.
              </p>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by title or content..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="w-full pl-12 py-3 bg-white border border-gray-200 rounded-lg shadow-sm text-sm
                placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/30 focus:border-[#2563eb]
                hover:border-gray-300 transition-colors duration-200"
            />
            {searchTerm && (
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium tabular-nums">
                {pagination.totalCount} result{pagination.totalCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2563eb]/30 focus:border-[#2563eb] text-gray-700"
            >
              <option value="">All types</option>
              <option value="Multiple Choice (Single)">Multiple Choice</option>
              <option value="Multiple Choice (Multiple)">Multiple Choice (Multiple)</option>
              <option value="True/False">True/False</option>
              <option value="Short Answer">Short Answer</option>
              <option value="Essay">Essay</option>
              <option value="Code">Code</option>
              <option value="Number">Number</option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-24"
          >
            <Loader2 className="h-10 w-10 text-[#2563eb] animate-spin mb-4" />
            <p className="text-sm font-medium text-gray-600">Loading your questions...</p>
          </motion.div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 bg-white rounded-xl border border-gray-200"
          >
            <p className="text-gray-600">Failed to load questions. Please try again.</p>
          </motion.div>
        ) : questions.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-16 px-6"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#2563eb]/10 mb-4">
              <BookOpen className="h-8 w-8 text-[#2563eb]" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No questions yet</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              Questions you create or add to assessments will appear here with their usage
              statistics.
            </p>
          </motion.div>
        ) : (
          <>
            {/* Questions List */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/80">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Question
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Screenings
                      </th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Answered
                      </th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Correct
                      </th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Wrong
                      </th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Avg
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((q, idx) => (
                      <motion.tr
                        key={q._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2, delay: idx * 0.02 }}
                        className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="py-4 px-4">
                          <div>
                            <h3 className="font-medium text-gray-900">
                              {q.title || truncate(q.content, 50)}
                            </h3>
                            <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">
                              {q.content || '—'}
                            </p>
                            {q.role && (
                              <div className="flex items-center gap-2 mt-1.5">
                                <span className="text-xs text-gray-500">{q.role}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600">{formatType(q.type)}</td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <ClipboardList className="h-4 w-4 text-[#2563eb] shrink-0" />
                            <span>{q.stats?.screeningsCount ?? 0}</span>
                            {Array.isArray(q.stats?.screeningsUsed) &&
                              q.stats.screeningsUsed.length > 0 && (
                                <span
                                  className="text-xs text-gray-400 truncate max-w-[120px]"
                                  title={q.stats.screeningsUsed.join(', ')}
                                >
                                  ({q.stats.screeningsUsed.slice(0, 2).join(', ')}
                                  {(q.stats.screeningsUsed?.length ?? 0) > 2 ? '…' : ''})
                                </span>
                              )}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                            <Users className="h-4 w-4 text-gray-400" />
                            {q.stats?.totalAnswered ?? 0}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center gap-1 text-sm text-emerald-600 font-medium">
                            <CheckCircle2 className="h-4 w-4" />
                            {q.stats?.correct ?? 0}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center gap-1 text-sm text-rose-600 font-medium">
                            <XCircle className="h-4 w-4" />
                            {q.stats?.wrong ?? 0}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center gap-1 text-sm font-medium text-[#2563eb]">
                            <BarChart3 className="h-4 w-4" />
                            {q.stats?.avgScorePercent != null ? `${q.stats.avgScorePercent}%` : '—'}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pagination.total > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing page {pagination.current} of {pagination.total} ({pagination.totalCount}{' '}
                  questions)
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.current <= 1}
                    className="p-2 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5 text-gray-600" />
                  </button>
                  <span className="text-sm font-medium text-gray-700">
                    {pagination.current} / {pagination.total}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.total, p + 1))}
                    disabled={pagination.current >= pagination.total}
                    className="p-2 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronRight className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

export default Library;
